import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { ArrowLeft, CreditCard, Loader2, Wallet, Clock, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DepositPage = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [deposits, setDeposits] = useState([]);
  const [methods, setMethods] = useState({ wise: {}, prc_remit: {} });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    method: 'wise',
    depositor_name: '',
    notes: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [balRes, depRes, methRes] = await Promise.all([
        axios.get(`${API_URL}/api/balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/deposits`, { withCredentials: true }),
        axios.get(`${API_URL}/api/deposit-methods`).catch(() => ({ data: { wise: {}, prc_remit: {} } }))
      ]);
      setBalance(balRes.data.balance || 0);
      setDeposits(depRes.data);
      setMethods(methRes.data || { wise: {}, prc_remit: {} });
    } catch (error) {
      console.error('Error fetching deposit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error(t('deposit.enterAmount'));
      return;
    }
    if (!form.depositor_name.trim()) {
      toast.error(t('deposit.enterDepositorName'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/deposits`, {
        amount: Number(form.amount),
        method: form.method,
        depositor_name: form.depositor_name.trim(),
        notes: form.notes.trim() || null
      }, { withCredentials: true });
      toast.success(t('deposit.success'));
      setForm({ amount: '', method: 'wise', depositor_name: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('deposit.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency', currency: 'JPY', maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { style: 'bg-yellow-500/15 text-yellow-400 border-yellow-400/40', label: t('deposit.statusPending') },
      approved: { style: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/40', label: t('deposit.statusApproved') },
      rejected: { style: 'bg-red-500/15 text-red-400 border-red-400/40', label: t('deposit.statusRejected') }
    };
    const c = config[status] || config.pending;
    return <Badge className={`${c.style} border`}>{c.label}</Badge>;
  };

  const getMethodLabel = (method) => {
    return method === 'prc_remit' ? 'PRC Remit' : 'Wise';
  };

  const currentMethod = methods[form.method] || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => navigate(-1)}
          data-testid="deposit-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-2">{t('deposit.title')}</h1>
          <p className="text-zinc-500">{t('deposit.subtitle')}</p>
        </div>

        {/* Balance Card */}
        <Card className="card-presage rounded-sm mb-8" data-testid="balance-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-500 text-sm mb-1">{t('deposit.balance')}</p>
                <p className="text-white font-mono text-3xl" data-testid="balance-amount">{formatCurrency(balance)}</p>
                <p className="text-zinc-600 text-xs mt-1">{t('deposit.balanceDesc')}</p>
              </div>
              <Wallet className="h-8 w-8 text-zinc-600" />
            </div>
          </CardContent>
        </Card>

        {/* Deposit Request Form */}
        <Card className="card-presage rounded-sm mb-8">
          <CardHeader>
            <CardTitle className="text-white text-lg">{t('deposit.requestTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-zinc-400">{t('deposit.amount')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-sm"
                  placeholder={t('deposit.amountPlaceholder')}
                  required
                  data-testid="deposit-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">{t('deposit.method')}</Label>
                <Select value={form.method} onValueChange={(v) => setForm(prev => ({ ...prev, method: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm" data-testid="deposit-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    <SelectItem value="wise">Wise</SelectItem>
                    <SelectItem value="prc_remit">PRC Remit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Show deposit method info from admin */}
              {(currentMethod.account_name || currentMethod.account_email || currentMethod.account_id || currentMethod.notes) && (
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-sm" data-testid="deposit-method-info">
                  <div className="flex items-start gap-2 mb-3">
                    <Info className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <p className="text-emerald-400 text-sm font-medium">{getMethodLabel(form.method)} {t('deposit.methodInfoTitle')}</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    {currentMethod.account_name && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('deposit.methodAccountName')}</span>
                        <span className="text-white font-mono">{currentMethod.account_name}</span>
                      </div>
                    )}
                    {currentMethod.account_email && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('deposit.methodAccountEmail')}</span>
                        <span className="text-white font-mono">{currentMethod.account_email}</span>
                      </div>
                    )}
                    {currentMethod.account_id && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('deposit.methodAccountId')}</span>
                        <span className="text-white font-mono">{currentMethod.account_id}</span>
                      </div>
                    )}
                    {currentMethod.currency && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('deposit.methodCurrency')}</span>
                        <span className="text-white font-mono">{currentMethod.currency}</span>
                      </div>
                    )}
                    {currentMethod.fee_rate && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('deposit.methodFee')}</span>
                        <span className="text-white font-mono">{currentMethod.fee_rate}%</span>
                      </div>
                    )}
                    {currentMethod.notes && (
                      <p className="text-zinc-400 mt-2 pt-2 border-t border-white/5">{currentMethod.notes}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-zinc-400">{t('deposit.depositorName')}</Label>
                <Input
                  type="text"
                  value={form.depositor_name}
                  onChange={(e) => setForm(prev => ({ ...prev, depositor_name: e.target.value }))}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-sm"
                  placeholder={t('deposit.depositorNamePlaceholder')}
                  required
                  data-testid="deposit-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">{t('deposit.notesLabel')}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-sm min-h-[80px]"
                  placeholder={t('deposit.notesPlaceholder')}
                  data-testid="deposit-notes-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm"
                disabled={submitting}
                data-testid="deposit-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('deposit.submitting')}
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('deposit.submit')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Deposit History */}
        <Card className="card-presage rounded-sm">
          <CardHeader>
            <CardTitle className="text-white text-lg">{t('deposit.history')}</CardTitle>
          </CardHeader>
          <CardContent>
            {deposits.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">{t('deposit.noDeposits')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deposits.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-sm border border-white/5"
                    data-testid={`deposit-history-${d.id}`}
                  >
                    <div>
                      <p className="text-white font-mono text-lg">{formatCurrency(d.amount)}</p>
                      <p className="text-zinc-500 text-xs mt-1">
                        {getMethodLabel(d.method)}
                        {' / '}
                        {d.depositor_name}
                      </p>
                      <p className="text-zinc-600 text-xs">
                        {new Date(d.created_at).toLocaleDateString('ja-JP')}
                      </p>
                      {d.admin_notes && (
                        <p className="text-zinc-400 text-xs mt-1">{d.admin_notes}</p>
                      )}
                    </div>
                    <div>{getStatusBadge(d.status)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DepositPage;
